import { CreateCounterpartyAddressBody } from "../schemas/counterpartyAddress.schema";
import { CreateCounterpartyContactBody } from "../schemas/counterpartyContact.schema";

const STATE_AND_POSTAL_CODE = /^([A-Za-z]{2})\s+(\d{5}(?:-\d{4})?)$/;

function parsedStateAndPostalCode(
    value: string,
): { state: string; postalCode: string } | null {
    const match = STATE_AND_POSTAL_CODE.exec(value);
    if (!match) {
        return null;
    }
    return { state: match[1].toUpperCase(), postalCode: match[2] };
}

export function parsePartyAddress(raw: string): CreateCounterpartyAddressBody {
    const normalized = raw
        .replace(/\s*\n\s*/g, ", ")
        .replace(/\s+/g, " ")
        .trim();
    const parts = normalized
        .split(",")
        .map((part) => part.trim())
        .filter((part) => part.length > 0);
    const stateAndPostalCode = parsedStateAndPostalCode(
        parts[parts.length - 1] ?? "",
    );

    if (stateAndPostalCode && parts.length >= 3) {
        return {
            street: parts.slice(0, -2).join(", "),
            city: parts[parts.length - 2],
            state: stateAndPostalCode.state,
            postalCode: stateAndPostalCode.postalCode,
            country: "US",
            isPrimary: true,
        };
    }

    if (stateAndPostalCode && parts.length === 2) {
        return {
            city: parts[0],
            state: stateAndPostalCode.state,
            postalCode: stateAndPostalCode.postalCode,
            country: "US",
            isPrimary: true,
        };
    }

    return {
        street: raw.trim(),
        isPrimary: true,
    };
}

export function splitPersonName(name: string): {
    firstName: string;
    lastName: string | null;
} {
    const parts = name
        .trim()
        .split(/\s+/)
        .filter((part) => part.length > 0);
    const [firstName, ...rest] = parts;
    return {
        firstName: firstName ?? name.trim(),
        lastName: rest.length > 0 ? rest.join(" ") : null,
    };
}

function contactBody({
    email,
    phoneNumber,
    contactName,
    fallbackContactName,
    isPrimary,
}: {
    email: string | null;
    phoneNumber: string | null;
    contactName: string | null;
    fallbackContactName: string | null;
    isPrimary: boolean;
}): CreateCounterpartyContactBody {
    const name =
        contactName ?? (email || phoneNumber ? fallbackContactName : null);
    return {
        email,
        phoneNumber,
        ...(name ? splitPersonName(name) : {}),
        isPrimary,
    };
}

export function groupContactDetails({
    emails,
    phoneNumbers,
    contactNames,
    fallbackContactName = null,
    isFirstPrimary,
}: {
    emails: string[];
    phoneNumbers: string[];
    contactNames: string[];
    fallbackContactName?: string | null;
    isFirstPrimary: boolean;
}): {
    first: CreateCounterpartyContactBody | null;
    extras: CreateCounterpartyContactBody[];
} {
    const [firstEmail, ...extraEmails] = emails;
    const [firstPhone, ...extraPhones] = phoneNumbers;
    const [firstContactName, ...extraContactNames] = contactNames;
    const hasFirst = Boolean(firstEmail || firstPhone || firstContactName);
    return {
        first: hasFirst
            ? contactBody({
                  email: firstEmail ?? null,
                  phoneNumber: firstPhone ?? null,
                  contactName: firstContactName ?? null,
                  fallbackContactName,
                  isPrimary: isFirstPrimary,
              })
            : null,
        extras: [
            ...extraEmails.map((email) =>
                contactBody({
                    email,
                    phoneNumber: null,
                    contactName: null,
                    fallbackContactName,
                    isPrimary: false,
                }),
            ),
            ...extraPhones.map((phoneNumber) =>
                contactBody({
                    email: null,
                    phoneNumber,
                    contactName: null,
                    fallbackContactName,
                    isPrimary: false,
                }),
            ),
            ...extraContactNames.map((contactName) =>
                contactBody({
                    email: null,
                    phoneNumber: null,
                    contactName,
                    fallbackContactName,
                    isPrimary: false,
                }),
            ),
        ],
    };
}
